import { Controller } from "@hotwired/stimulus";

// Kick's category search rejects anything shorter, and the server applies the
// same floor (StreamCommand-75yu).
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 250;

function csrfToken() {
  return (document.querySelector('meta[name="csrf-token"]') || {}).content || "";
}

function esc(str) {
  const el = document.createElement("span");
  el.textContent = String(str ?? "");
  return el.innerHTML;
}

// Shows what each platform made of the game the streamer picked, and lets them
// settle the cases it could not. Collapsed while every platform agrees; expanded
// automatically the moment one does not, because that is the only moment the
// streamer actually has something to decide.
//
// Nothing here ever chooses on the user's behalf. A platform that could not
// match offers its nearest candidates and keeps its previous category until the
// user picks -- a stale category is recoverable, a confidently wrong one is not.
//
// Every string comes in through stringsValue rather than living here, because a
// Stimulus controller has no t(); the component translates and hands them over.
export default class extends Controller {
  static targets = ["body"];
  static values = {
    resolveUrl: String,
    searchUrl: String,
    kickRequestUrl: String,
    tenant: String,
    platforms: Array,
    youtubeCategories: Object,
    labels: Object,
    strings: Object,
  };

  connect() {
    this._chosen = "";
    this._verdicts = {};
    this._youtube = null;
    this._expanded = false;
    this._timers = {};
    this._resolveId = 0;
    this._render();
  }

  disconnect() {
    Object.values(this._timers).forEach(clearTimeout);
  }

  // game-search:selected -- the streamer settled on a name upstream.
  gamePicked(event) {
    const name = (event.detail && event.detail.name) || "";
    if (!name) return;

    this._chosen = name;
    this._resolve(name);
  }

  expand() {
    this._expanded = true;
    this._render();
  }

  async _resolve(name) {
    this._verdicts = {};
    this._render({ pending: true });

    const requestId = ++this._resolveId;
    try {
      const url = `${this.resolveUrlValue}?name=${encodeURIComponent(
        name,
      )}&tenant_slug=${encodeURIComponent(this.tenantValue)}`;
      const resp = await fetch(url, {
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
      });
      if (requestId !== this._resolveId) return;
      const body = resp.ok ? await resp.json() : { platforms: [] };
      if (requestId !== this._resolveId) return;

      (body.platforms || []).forEach((verdict) => {
        this._verdicts[verdict.platform] = verdict;
      });
    } catch {
      // A failed resolve is not a failed game change -- the server resolves
      // again on submit. Every line falls back to "unavailable", which keeps
      // the previous category and says so.
      if (requestId !== this._resolveId) return;
    }

    // Anything short of every platform agreeing is worth showing outright.
    this._expanded = this._diverged();
    this._render();
  }

  _diverged() {
    return this._gamePlatforms().some((platform) => {
      const verdict = this._verdicts[platform];
      return !verdict || verdict.status !== "matched";
    });
  }

  // YouTube is excluded: it has no game catalog, so it can never agree or
  // disagree about a game -- it just keeps its own category.
  _gamePlatforms() {
    return this.platformsValue
      .map((row) => row.platform)
      .filter((platform) => platform !== "youtube");
  }

  _has(platform) {
    return this.platformsValue.some((row) => row.platform === platform);
  }

  _currentFor(platform) {
    const row = this.platformsValue.find((r) => r.platform === platform);
    return (row && row.current) || "";
  }

  _label(platform) {
    return this.labelsValue[platform] || platform;
  }

  // Rails-style %{name} interpolation, so the locale file keeps control of
  // where a value lands in the sentence.
  _t(key, vars = {}) {
    const raw = this.stringsValue[key] || "";
    return raw.replace(/%\{(\w+)\}/g, (_match, name) =>
      Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : "",
    );
  }

  // ---- rendering -------------------------------------------------------

  _render(opts = {}) {
    if (!this.hasBodyTarget) return;

    if (!this._chosen) {
      this.bodyTarget.innerHTML = this._note(this._t("idle"));
      return;
    }
    if (opts.pending) {
      this.bodyTarget.innerHTML = this._note(this._t("checking", { game: this._chosen }));
      return;
    }

    this.bodyTarget.innerHTML = this._expanded ? this._expandedHtml() : this._collapsedHtml();
  }

  _note(text) {
    return `<p class="text-[0.82rem] text-base-content/60">${esc(text)}</p>`;
  }

  _collapsedHtml() {
    const names = this._gamePlatforms().map((platform) => this._label(platform));
    const youtube = this._has("youtube")
      ? `<span class="opacity-40">&middot;</span><span>${esc(
          this._t("youtube_summary", { name: this._youtubeLabel() }),
        )}</span>`
      : "";

    return `<div class="flex flex-wrap items-center gap-2 text-[0.82rem]">
      <span class="text-success">${esc(this._t("agreed", { platforms: names.join(", ") }))}</span>
      ${youtube}
      <span class="opacity-40">&middot;</span>
      <button type="button" class="link link-hover text-[0.82rem]" data-action="category-picker#expand">${esc(
        this._t("expand"),
      )}</button>
    </div>`;
  }

  _expandedHtml() {
    const lines = this._gamePlatforms()
      .map((platform) => this._lineHtml(platform))
      .join("");
    const youtube = this._has("youtube") ? this._youtubeLineHtml() : "";
    return `<div class="flex flex-col gap-2">${lines}${youtube}</div>`;
  }

  _lineHtml(platform) {
    const label = this._label(platform);
    const verdict = this._verdicts[platform] || { status: "unavailable" };

    if (verdict.status === "matched") {
      return this._row(
        label,
        esc(verdict.category.name),
        this._state(platform, "success", this._t("matched")),
      );
    }

    if (verdict.status === "unavailable") {
      return this._row(
        label,
        `<span class="opacity-60">${esc(this._t("unavailable_value"))}</span>`,
        this._state(platform, "error", this._t("unavailable_state")),
        `<p class="col-span-full text-[0.78rem] text-base-content/60">${esc(
          this._t("unavailable_hint", { platform: label }),
        )}</p>`,
      );
    }

    return this._row(
      label,
      `<span class="opacity-60">${esc(this._t("no_match_value", { game: this._chosen }))}</span>`,
      this._state(platform, "warning", this._t("no_match_state")),
      this._searchHtml(platform, verdict.suggestions || []),
    );
  }

  _state(platform, tone, text) {
    return `<span class="text-${tone}" data-state-for="${platform}">${esc(text)}</span>`;
  }

  _row(label, value, state, extra = "") {
    return `<div class="grid grid-cols-[7rem_1fr_auto] items-center gap-2 text-[0.85rem]">
      <span class="font-semibold text-base-content/70">${esc(label)}</span>
      <span>${value}</span>
      ${state}
      ${extra}
    </div>`;
  }

  // Candidates are seeded from the resolve call and the box stays searchable,
  // so a near miss is one click away without ever being applied for the user.
  _searchHtml(platform, suggestions) {
    const listId = `catalog-${platform}`;
    const options = suggestions.map((s) => this._optionHtml(s)).join("");
    return `<div class="col-span-full">
      <input type="text" class="input input-bordered input-sm w-full" list="${listId}"
             placeholder="${esc(
               this._t("search_placeholder", { platform: this._label(platform) }),
             )}"
             aria-label="${esc(this._t("search_label", { platform: this._label(platform) }))}"
             data-platform="${platform}"
             data-action="input->category-picker#searchCatalog change->category-picker#chooseCatalog">
      <datalist id="${listId}">${options}</datalist>
      <input type="hidden" name="categories[${platform}]" data-category-field="${platform}">
      ${platform === "kick" ? this._kickRequestHtml() : ""}
    </div>`;
  }

  // A genuinely absent title is not something this control can fix -- Kick's
  // write API takes an integer category id and nothing else, so the escalation
  // path is Kick's own. Naming it beats letting the streamer conclude the
  // search is broken (StreamCommand-75yu contract point 4).
  _kickRequestHtml() {
    return `<a class="link link-hover block mt-1 text-[0.78rem] text-base-content/60"
       href="${esc(this.kickRequestUrlValue)}" target="_blank" rel="noopener">${esc(
         this._t("kick_request"),
       )}</a>`;
  }

  _optionHtml(entry) {
    return `<option value="${esc(entry.name)}" data-id="${esc(entry.id)}"></option>`;
  }

  _youtubeLabel() {
    const id = this._youtube === null ? this._currentFor("youtube") : this._youtube;
    return this.youtubeCategoriesValue[id] || this._t("not_set");
  }

  // YouTube's list is fixed and short, so it is a plain select -- there is
  // nothing to search, and nothing a game name could ever resolve onto.
  _youtubeLineHtml() {
    const selected = this._youtube === null ? this._currentFor("youtube") : this._youtube;
    const options = Object.entries(this.youtubeCategoriesValue)
      .map(
        ([id, name]) =>
          `<option value="${esc(id)}"${id === selected ? " selected" : ""}>${esc(name)}</option>`,
      )
      .join("");
    return `<div class="grid grid-cols-[7rem_1fr_auto] items-center gap-2 text-[0.85rem]">
      <span class="font-semibold text-base-content/70">${esc(this._label("youtube"))}</span>
      <select class="select select-bordered select-sm w-full" aria-label="${esc(
        this._t("youtube_label"),
      )}"
              name="categories[youtube]" data-action="change->category-picker#chooseYoutube">
        <option value="">${esc(this._t("not_set"))}</option>
        ${options}
      </select>
      <span class="opacity-60">${esc(this._t("youtube_state"))}</span>
    </div>`;
  }

  // ---- per-platform search --------------------------------------------

  searchCatalog(event) {
    const input = event.currentTarget;
    const platform = input.dataset.platform;
    const query = input.value.trim();

    // Typing past a chosen name unpicks it, rather than leaving a stale id
    // attached to text that no longer says what it did.
    this._clearPick(platform);

    clearTimeout(this._timers[platform]);
    if (query.length < MIN_QUERY_LENGTH) return;
    this._timers[platform] = setTimeout(
      () => this._fetchCatalog(input, platform, query),
      DEBOUNCE_MS,
    );
  }

  chooseCatalog(event) {
    const input = event.currentTarget;
    const platform = input.dataset.platform;
    const option = this._optionFor(input, input.value);
    if (!option) return;

    const field = this._fieldFor(platform);
    if (field) field.value = option.dataset.id;
    this._setState(platform, "success", this._t("matched"));
  }

  chooseYoutube(event) {
    this._youtube = event.currentTarget.value;
  }

  _optionFor(input, value) {
    const list = input.list;
    if (!list) return null;
    return Array.from(list.options).find((o) => o.value === value) || null;
  }

  // The hidden field is the state, so clearing it is all that unpicking means.
  // Updating in place rather than re-rendering is deliberate: a re-render would
  // destroy the input being typed into along with the field just written to.
  _clearPick(platform) {
    const field = this._fieldFor(platform);
    if (!field || !field.value) return;
    field.value = "";
    this._setState(platform, "warning", this._t("no_match_state"));
  }

  _setState(platform, tone, text) {
    const span = this.element.querySelector(`[data-state-for="${platform}"]`);
    if (!span) return;
    span.className = `text-${tone}`;
    span.textContent = text;
  }

  _fieldFor(platform) {
    return this.element.querySelector(`[data-category-field="${platform}"]`);
  }

  async _fetchCatalog(input, platform, query) {
    try {
      const url = `${this.searchUrlValue}?q=${encodeURIComponent(
        query,
      )}&platform=${encodeURIComponent(platform)}&tenant_slug=${encodeURIComponent(
        this.tenantValue,
      )}`;
      const resp = await fetch(url, {
        headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
      });
      if (!resp.ok) return;
      const { games } = await resp.json();
      const list = input.list;
      if (!list) return;
      list.innerHTML = (games || []).map((g) => this._optionHtml(g)).join("");
    } catch {
      // Leaving the previous options in place beats emptying the list under
      // the user mid-type.
    }
  }
}
